import { Status, StatusBadge, TextCell, Tooltip, TooltipContent, TooltipTrigger } from "@bcc-ui/ui-components";
import classNames from "classnames";
import { TFunction } from "i18next";
import * as React from "react";
import { Column } from "react-table";

import { isStringArray } from "./AllReportsTable";
import style from "./erasure-reports-table.scss";
import DateCell from "components/table/DateCell";
import { Path } from "domain/reports";
import { Report, ReportService } from "services/report/erasure/ReportService";
import { getReportUuidsToBeDeleted } from "services/table/tableRepository";
import layoutStyle from "styles/layout.scss";
import { getFaultCodeTranslation } from "utils/commonFunctions";
import { formatPrice } from "utils/format";

import checkIcon from "assets/images/icons/checkMarkInCircle.svg";
import crossIcon from "assets/images/icons/cross.svg";

import testIds from "testIds.json";

export interface ColumnCreatorParams {
    t: TFunction;
    translatePath: (path: string) => string;
}

export const AUTOPILOT_RECHECK_STATUS_MAP = new Map<string, [Status, string]>([
    ["VALIDATION_STARTED", [Status.NEUTRAL, "Autopilot.recheckStatus.started"]],
    ["VALIDATION_FINISHED", [Status.SUCCESS, "Autopilot.recheckStatus.finished"]],
    ["VALIDATION_FAILED", [Status.ERROR, "Autopilot.recheckStatus.failed"]],
]);

export const UUID_PATTERN = new RegExp("^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$");

export const AUTOPILOT_STATUS_COLOR_MAP = new Map<string, Status>([
    ["enrolled", Status.WARNING],
    ["not enrolled", Status.SUCCESS],
    ["not processed", Status.NEUTRAL],
    ["not available", Status.ERROR],
    ["unknown", Status.NEUTRAL],
    ["no license", Status.ERROR],
    ["recheck failed", Status.ERROR],
]);

export const ERASURE_STATE_TEXT_TO_STATUS = new Map([
    ["successful", Status.SUCCESS],
    ["failed", Status.ERROR],
]);

export function isRowDisabled(uuid: string): boolean {
    return getReportUuidsToBeDeleted().includes(uuid);
}

export const getFirstStringArrayValueOrEmptyString = (report: Report, path: string): string => {
    const entry = report.entries.find((e) => e.path === path);
    const values = entry != null ? entry.values : null;
    if (Array.isArray(values) && values.length > 0) {
        // Assuming that the first value of the array is the most interesting one, i.e., the latest result
        return isStringArray(values) ? values[0] : "";
    }
    return "";
};

export const getStringArrayValuesOrEmptyArray = (report: Report, path: string): string[] => {
    const entry = report.entries.find((e) => e.path === path);
    const values = entry != null ? entry.values : null;
    if (Array.isArray(values) && values.length > 0) {
        return values.map((value) => value.toString());
    }
    return [];
};

export const createProductColumn = (params: ColumnCreatorParams): Column<Report> => {
    const identifier = "product";
    return {
        Header: <TextCell>{params.t("ErasureReportsTable.product")}</TextCell>,
        accessor: (report: Report): Report => report,
        width: 120,
        id: identifier,
        Cell: ({ cell: { value: report } }: { cell: { value: Report } }) => (
            <TextCell disabled={isRowDisabled(report.uuid)}>{ReportService.extractProductName(report)}</TextCell>
        ),
    };
};

export const createUserColumn = (localizationKey: string, path: Path, params: ColumnCreatorParams): Column<Report> => {
    interface UserColumn {
        name: string;
        uuid: string;
    }
    return {
        Header: <TextCell>{params.t(localizationKey)}</TextCell>,
        accessor: (report: Report): UserColumn => {
            let name = getFirstStringArrayValueOrEmptyString(report, path);
            if (UUID_PATTERN.test(name)) {
                name = params.t("Common.unknownUser", { uuid: name });
            }
            return { name, uuid: report.uuid };
        },
        id: path,
        width: 140,
        Cell: ({ cell: { value } }: { cell: { value: UserColumn } }) => (
            <TextCell disabled={isRowDisabled(value.uuid)}>{value.name}</TextCell>
        ),
    };
};

export const createDateColumn = (options: { path: string; header: string; width?: number }): Column<Report> => {
    const identifier = options.path;
    return {
        Header: <TextCell>{options.header}</TextCell>,
        accessor: (report: Report): Report => report,
        id: identifier,
        width: options.width,
        Cell: ({ cell: { value: report } }: { cell: { value: Report } }) => {
            if (options.path == "date") {
                return <DateCell value={report.date} disabled={isRowDisabled(report.uuid)} />;
            } else if (options.path == Path.EDITED_DATE) {
                return report.editedDate != null ? (
                    <DateCell value={report.editedDate} disabled={isRowDisabled(report.uuid)} />
                ) : (
                    ""
                );
            }
            const date: string = getFirstStringArrayValueOrEmptyString(report, options.path);
            if (date == "") {
                return "";
            }
            return <DateCell value={date} disabled={isRowDisabled(report.uuid)} />;
        },
    };
};

export const createReportDateColumn = (params: ColumnCreatorParams) =>
    createDateColumn({
        path: Path.DATE,
        header: params.t("Common.creationDate"),
        width: 150,
    });

export const createEditedDateColumn = (params: ColumnCreatorParams) =>
    createDateColumn({
        path: Path.EDITED_DATE,
        header: params.t("Common.editedDate"),
        width: 150,
    });

export const createFaultCodeRunDateColumn = (params: ColumnCreatorParams) =>
    createDateColumn({
        path: Path.NTF_RESULT_REPORT_DATE,
        header: params.t("DiagnosticReportsTable.myDeviceHealth.faultCategoryRunDate"),
        width: 150,
    });

export const createFaultCodeExpirationDateColumn = (params: ColumnCreatorParams) =>
    createDateColumn({
        path: Path.NTF_OVERALL_REFERENCE_CODE_EXPIRATION,
        header: params.t("DiagnosticReportsTable.myDeviceHealth.referenceCodeExpiration"),
        width: 230,
    });

export const createPriceColumn = (path: string, params: ColumnCreatorParams): Column<Report> => {
    interface PriceColumn {
        price: number[];
        uuid: string;
    }
    return {
        Header: <TextCell>{params.translatePath(path)}</TextCell>,
        accessor: (report: Report): PriceColumn => {
            const entry = report.entries.find((e) => e.path === path);
            const values = entry != null ? entry.values : null;
            if (Array.isArray(values) && !isStringArray(values) && values.length > 0) {
                return {
                    price: values,
                    uuid: report.uuid,
                };
            }
            return {
                price: [],
                uuid: report.uuid,
            };
        },
        id: path,
        width: 120,
        Cell: ({ cell: { value: value } }: { cell: { value: PriceColumn } }) => (
            <TextCell disabled={isRowDisabled(value.uuid)}>
                {value.price.map((each) => formatPrice(each)).join(", ")}
            </TextCell>
        ),
    };
};

export const createErasureStatusColumn = (params: ColumnCreatorParams): Column<Report> => {
    const path = Path.ERASURE_STATE;
    interface StatusColumn {
        statuses: string[];
        uuid: string;
    }
    return {
        Header: <TextCell>{params.translatePath(path)}</TextCell>,
        accessor: (report: Report): StatusColumn => {
            return { statuses: getStringArrayValuesOrEmptyArray(report, path), uuid: report.uuid };
        },
        id: path,
        width: 150,
        Cell: ({ cell: { value: value } }: { cell: { value: StatusColumn } }) => {
            if (value.statuses) {
                return (
                    <div className={style.statusBadgeContainer}>
                        {value.statuses.map((status, index) => {
                            const titleText = `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
                            return (
                                <React.Fragment key={index}>
                                    <StatusBadge
                                        value={titleText}
                                        status={
                                            ERASURE_STATE_TEXT_TO_STATUS.get(status.trim().toLowerCase()) ??
                                            Status.WARNING
                                        }
                                        className={classNames({
                                            [layoutStyle.disabledContainer]: isRowDisabled(value.uuid),
                                        })}
                                    />
                                    {index < value.statuses.length - 1 && "/"}
                                </React.Fragment>
                            );
                        })}
                    </div>
                );
            } else {
                return "";
            }
        },
    };
};

export const createAutopilotStatusColumn = (params: ColumnCreatorParams): Column<Report> => {
    const path = Path.AUTOPILOT_STATE;
    interface StatusColumn {
        status: string;
        uuid: string;
    }
    return {
        Header: <TextCell>{params.translatePath(path)}</TextCell>,
        accessor: (report: Report): StatusColumn => {
            return { status: getFirstStringArrayValueOrEmptyString(report, path), uuid: report.uuid };
        },
        id: path,
        width: 230,
        Cell: ({ cell: { value: value } }: { cell: { value: StatusColumn } }) => {
            return (
                value.status != null &&
                value.status != "" && (
                    <StatusBadge
                        className={isRowDisabled(value.uuid) ? layoutStyle.disabledContainer : undefined}
                        value={value.status}
                        status={AUTOPILOT_STATUS_COLOR_MAP.get(value.status.trim().toLowerCase()) ?? Status.NEUTRAL}
                    />
                )
            );
        },
    };
};

export const createAutopilotRecheckPreviousEnrollmentStatusColumn = (params: ColumnCreatorParams): Column<Report> => {
    const path = Path.AUTOPILOT_RECHECK_PREVIOUS_ENROLLMENT_STATUS;
    interface StatusColumn {
        status: string;
        uuid: string;
    }
    return {
        Header: <TextCell>{params.translatePath(path)}</TextCell>,
        accessor: (report: Report): StatusColumn => {
            return { status: getFirstStringArrayValueOrEmptyString(report, path), uuid: report.uuid };
        },
        id: path,
        width: 230,
        Cell: ({ cell: { value: value } }: { cell: { value: StatusColumn } }) => {
            return (
                value.status != null &&
                value.status != "" && (
                    <StatusBadge
                        value={value.status}
                        className={isRowDisabled(value.uuid) ? layoutStyle.disabledContainer : undefined}
                        status={value.status.trim().toLowerCase() === "enrolled" ? Status.WARNING : Status.SUCCESS}
                    />
                )
            );
        },
    };
};

export const createAutopilotRecheckStatusColumn = (params: ColumnCreatorParams): Column<Report> => {
    const path = Path.AUTOPILOT_RECHECK_STATUS;
    interface RecheckStatusColumn {
        recheckStatus: string;
        uuid: string;
    }
    return {
        Header: <TextCell>{params.translatePath(path)}</TextCell>,
        id: path,
        width: 225,
        accessor: (report: Report): RecheckStatusColumn => {
            return { recheckStatus: getFirstStringArrayValueOrEmptyString(report, path), uuid: report.uuid };
        },
        Cell: ({ cell: { value: value } }: { cell: { value: RecheckStatusColumn } }) => {
            if (AUTOPILOT_RECHECK_STATUS_MAP.has(value.recheckStatus)) {
                const [badgeStatus, translationKey] = AUTOPILOT_RECHECK_STATUS_MAP.get(value.recheckStatus)!;
                return (
                    <StatusBadge
                        className={isRowDisabled(value.uuid) ? layoutStyle.disabledContainer : undefined}
                        value={params.t(translationKey)}
                        status={badgeStatus}
                    />
                );
            }
        },
    };
};

export const createAutopilotRecheckStatusDateColumn = (params: ColumnCreatorParams) =>
    createDateColumn({
        path: Path.AUTOPILOT_RECHECK_STATUS_DATE,
        header: params.translatePath(Path.AUTOPILOT_RECHECK_STATUS_DATE),
        width: 260,
    });

export const createAutopilotEnrollmentQueryTimestampColumn = (params: ColumnCreatorParams) =>
    createDateColumn({
        path: Path.AUTOPILOT_ENROLLMENT_QUERY_TIMESTAMP,
        header: params.translatePath(Path.AUTOPILOT_ENROLLMENT_QUERY_TIMESTAMP),
        width: 230,
    });

export const createAutopilotRecheckTimestampColumn = (params: ColumnCreatorParams) =>
    createDateColumn({
        path: Path.AUTOPILOT_RECHECK_TIMESTAMP,
        header: params.translatePath(Path.AUTOPILOT_RECHECK_TIMESTAMP),
        width: 250,
    });

const renderVerificationStatusIcon = (successful: boolean, text: string) => (
    <TextCell>
        <Tooltip placement={"top"}>
            <TooltipContent testId={testIds.common.primaryView.table.cellTooltip.itself}>{text}</TooltipContent>
            <TooltipTrigger testId={testIds.common.primaryView.table.cellTooltip.trigger}>
                <img src={successful ? checkIcon : crossIcon} alt={text} />
            </TooltipTrigger>
        </Tooltip>
    </TextCell>
);

export const createVerifiedColumn = (params: ColumnCreatorParams): Column<Report> => {
    const path = Path.VERIFIED;
    return {
        Header: <TextCell>{params.translatePath(path)}</TextCell>,
        accessor: (report: Report): Report => report,
        id: path,
        width: 150,
        Cell: ({ cell: { value: value } }: { cell: { value: Report } }) => {
            const successful: boolean = getStringArrayValuesOrEmptyArray(value, path)
                .map((e) => e.trim().toLowerCase())
                .every((e) => e === "true");
            const text = successful
                ? params.t("ErasureReportsTable.passedVerification")
                : params.t("ErasureReportsTable.failedVerification");
            return renderVerificationStatusIcon(successful, text);
        },
    };
};

export const createCustomIntegrityVerifiedColumn = (params: ColumnCreatorParams): Column<Report> => {
    const path = Path.CUSTOM_INTEGRITY_VERIFIED;
    return {
        Header: <TextCell>{params.translatePath(path)}</TextCell>,
        accessor: (report: Report): Report => report,
        id: path,
        width: 150,
        Cell: ({ cell: { value: value } }: { cell: { value: Report } }) => {
            const values = getStringArrayValuesOrEmptyArray(value, path)
                .map((e) => e.trim())
                .filter((e) => e !== "");
            if (values.length === 0) {
                return <TextCell>{""}</TextCell>;
            }
            const successful = values.every((e) => e.toLowerCase() === "successful");
            const text = successful
                ? params.t("ErasureReportsTable.passedVerification")
                : params.t("ErasureReportsTable.failedVerification");
            return renderVerificationStatusIcon(successful, text);
        },
    };
};
export const createFaultStatusColumn = (
    path: string,
    header: string,
    params: ColumnCreatorParams,
    width?: number
): Column<Report> => {
    return {
        Header: <TextCell>{params.t(header)}</TextCell>,
        accessor: (report: Report): Report => report,
        id: path,
        width: width,
        Cell: ({ cell: { value } }: { cell: { value: Report } }) => {
            const arrayValue: string = getFirstStringArrayValueOrEmptyString(value, path);
            if (arrayValue === "") {
                return "";
            }
            const faultFound: boolean = arrayValue.trim().toLowerCase() === "true";
            const text = faultFound
                ? params.t("DiagnosticReportsTable.myDeviceHealth.faultFound")
                : params.t("DiagnosticReportsTable.myDeviceHealth.faultNotFound");
            return (
                <TextCell>
                    <Tooltip placement={"top"}>
                        <TooltipContent testId={testIds.common.primaryView.table.cellTooltip.itself}>
                            {text}
                        </TooltipContent>
                        <TooltipTrigger testId={testIds.common.primaryView.table.cellTooltip.trigger}>
                            <img src={faultFound ? crossIcon : checkIcon} alt={text} />
                        </TooltipTrigger>
                    </Tooltip>
                </TextCell>
            );
        },
    };
};

export const createFaultFoundColumn = (params: ColumnCreatorParams) =>
    createFaultStatusColumn(
        Path.NTF_RESULT_FAULT_FOUND,
        "DiagnosticReportsTable.myDeviceHealth.faultNotFound",
        params,
        150
    );

export const createOverallFaultFoundColumn = (params: ColumnCreatorParams) =>
    createFaultStatusColumn(
        Path.NTF_OVERALL_FAULT_FOUND,
        "DiagnosticReportsTable.myDeviceHealth.overallFaultNotFound",
        params,
        200
    );

export const createFaultCodeColumn = (params: ColumnCreatorParams): Column<Report> => {
    const path = Path.NTF_RESULT_FAULT_CODE;
    return {
        Header: <TextCell>{params.translatePath(path)}</TextCell>,
        accessor: (report: Report): Report => report,
        id: path,
        width: 150,
        Cell: ({ cell: { value } }: { cell: { value: Report } }) => {
            const faultCode = getFaultCodeTranslation(getFirstStringArrayValueOrEmptyString(value, path));
            const translatedFaultCode = faultCode ? params.t(faultCode) : "";
            return <TextCell disabled={isRowDisabled(value.uuid)}>{translatedFaultCode}</TextCell>;
        },
    };
};
